import { Head, router, usePage } from '@inertiajs/react'
import type { ReactNode } from 'react'
import PersistentLayout from '@/layouts/PersistentLayout'
import DashboardLayout, { type DashboardSharedProps } from '@/layouts/DashboardLayout'
import RewardSection, { type RewardSectionProps } from '@/components/dashboard/RewardSection'
import { GroupCard, GroupHeading } from '@/components/dashboard/GroupCard'

type ExaminationSummary = {
  id: number
  start_at: string
  start_at_secondary: string | null
  patient_name: string | null
  department_name: string | null
  path: string
}

type TodayScheduleItem = {
  type: 'examination' | 'standby'
  time_range: string
  time_range_secondary: string | null
  examination: ExaminationSummary | null
}

type ReplyAwaitingConsultation = {
  id: number
  consultation_id: number
  patient_name: string | null
  last_message_at: string
  path: string
}

type ScheduleDate = {
  value: string
  prev_value: string
  next_value: string
  label: string
  is_today: boolean
  is_past: boolean
}

type SlotShortage = {
  date_label: string
  time_range: string
  missing: number
  start_iso: string
  end_iso: string
  calendar_path: string
}

type DoctorHomeProps = DashboardSharedProps & {
  reply_awaiting_consultations: ReplyAwaitingConsultation[]
  today_schedule: TodayScheduleItem[]
  schedule_date: ScheduleDate
  upcoming_examinations: ExaminationSummary[]
  pending_examinations: ExaminationSummary[]
  reward_section: RewardSectionProps | null
  slot_shortages: SlotShortage[]
}

function TodaySchedule({ items, scheduleHref, scheduleDate }: {
  items: TodayScheduleItem[]
  scheduleHref: string
  scheduleDate: ScheduleDate
}) {
  if (items.length === 0) {
    return (
      <p className="text-sm text-gray-500 px-1 py-2">
        {scheduleDate.is_today ? '今日' : 'この日'}の待機枠はありません。
        {/* 過去の日付には枠を開放できないため案内を出さない */}
        {!scheduleDate.is_past && (
          <a href={scheduleHref} className="text-blue-600 hover:underline ml-1">医師スケジュールで枠を開放できます</a>
        )}
      </p>
    )
  }
  return (
    <ul className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-96 overflow-y-auto">
      {items.map((item, i) => (
        <li key={i}>
          {item.type === 'standby' ? (
            <div className="flex items-center gap-3 px-4 py-3">
              <span className="text-xs font-semibold text-[#1a1f36] whitespace-nowrap">
                {item.time_range}
                {item.time_range_secondary && <span className="ml-1 text-[10px] font-normal text-gray-400">{item.time_range_secondary}</span>}
              </span>
              <span className="text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">待機（予約なし）</span>
            </div>
          ) : (
            <a href={item.examination?.path} target="_blank" rel="noopener" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
              <span className="text-xs font-semibold text-[#1a1f36] whitespace-nowrap shrink-0">
                {item.time_range}
                {item.time_range_secondary && <span className="ml-1 text-[10px] font-normal text-gray-400">{item.time_range_secondary}</span>}
              </span>
              <span className="text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 whitespace-nowrap shrink-0">診療</span>
              {/* 患者名を優先して表示し、長い診療科目名は上限つきで省略する */}
              <span className="flex-1 min-w-[5.5rem] truncate text-xs text-gray-700">
                {item.examination?.patient_name ? `${item.examination.patient_name} 様` : '患者情報なし'}
              </span>
              <span className="min-w-0 text-[11px] text-gray-500 truncate max-w-[8rem] text-right">{item.examination?.department_name}</span>
            </a>
          )}
        </li>
      ))}
    </ul>
  )
}

function ExaminationList({ items, emptyText }: { items: ExaminationSummary[]; emptyText: string }) {
  if (items.length === 0) {
    return <p className="text-sm text-gray-500 px-1 py-2">{emptyText}</p>
  }
  return (
    <ul className="divide-y divide-gray-100 bg-white border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
      {items.map((e) => (
        <li key={e.id}>
          <a href={e.path} target="_blank" rel="noopener" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
            <span className="text-xs font-semibold text-[#1a1f36] whitespace-nowrap shrink-0">
              {e.start_at}
              {e.start_at_secondary && <span className="ml-1 text-[10px] font-normal text-gray-400">{e.start_at_secondary}</span>}
            </span>
            {/* 患者名を優先して表示し、長い診療科目名は上限つきで省略する */}
            <span className="flex-1 min-w-[5.5rem] truncate text-xs text-gray-700">{e.patient_name ? `${e.patient_name} 様` : '患者情報なし'}</span>
            <span className="min-w-0 text-[11px] text-gray-500 truncate max-w-[8rem] text-right">{e.department_name}</span>
          </a>
        </li>
      ))}
    </ul>
  )
}

/**
 * 医師向けダッシュボードホーム (#1070)
 * 骨格段階。通知・当日の診療予定・報酬は後続（#1072, #1096）でここに載る。
 */
export default function DoctorHome() {
  const {
    nav_paths, today_schedule, schedule_date, upcoming_examinations, pending_examinations,
    reply_awaiting_consultations, reward_section, slot_shortages,
  } = usePage().props as unknown as DoctorHomeProps

  const hasActionItems = pending_examinations.length > 0 || reply_awaiting_consultations.length > 0

  // 日付ナビと報酬の月選択が互いの選択を打ち消さないよう、両方のパラメータを持ち回る。
  // dateを省略（空文字）すると今日に戻る
  const goScheduleDate = (date: string) => {
    router.get(nav_paths.home, {
      ...(reward_section ? { reward_month: reward_section.selected_month } : {}),
      ...(date ? { schedule_date: date } : {}),
    }, { preserveScroll: true })
  }
  const dateNavButtonClass =
    'px-2 py-0.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-100 cursor-pointer'

  // 不足時間帯の即時開放 (#1076)。カレンダーのドラッグ選択と同じサービスで枠を作る
  const openShortageSlots = (shortage: SlotShortage) => {
    if (!window.confirm(`${shortage.date_label} ${shortage.time_range} の予約可能時間を開放します。よろしいですか？`)) return
    router.post('/dashboard/slot_openings', { start: shortage.start_iso, end: shortage.end_iso }, { preserveScroll: true })
  }

  return (
    <>
      <Head title="ダッシュボード" />
      <GroupCard header={<GroupHeading title="要対応" />} tone="attention">
        {!hasActionItems && (
          <p className="text-sm text-gray-500">現在対応が必要な項目はありません。</p>
        )}
        {/* 2つとも出ている場合は横並び。片方だけなら全幅 */}
        <div className="grid gap-6 lg:grid-cols-2 items-start">
          {pending_examinations.length > 0 && (
            <div className={reply_awaiting_consultations.length > 0 ? '' : 'lg:col-span-2'}>
              <h3 className="text-base font-semibold text-red-600 mb-2">診察時間が終了した診療</h3>
              <p className="text-xs text-gray-500 mb-2">処方提案の入力をお願いします。</p>
              <ExaminationList items={pending_examinations} emptyText="" />
            </div>
          )}
          {reply_awaiting_consultations.length > 0 && (
            <div className={pending_examinations.length > 0 ? '' : 'lg:col-span-2'}>
              <h3 className="text-base font-semibold text-amber-600 mb-2">返信待ちの医療相談</h3>
              <p className="text-xs text-gray-500 mb-2">担当中の相談に患者から新しいメッセージが届いています。</p>
              <ul className="divide-y divide-gray-100 bg-white border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                {reply_awaiting_consultations.map((c) => (
                  <li key={c.id}>
                    <a href={c.path} target="_blank" rel="noopener" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                      <span className="text-xs font-semibold text-[#1a1f36] whitespace-nowrap">相談 {c.consultation_id}</span>
                      <span className="flex-1 min-w-0 truncate text-xs text-gray-700">{c.patient_name ? `${c.patient_name} 様` : '患者情報なし'}</span>
                      <span className="text-[11px] text-gray-500 whitespace-nowrap">最終メッセージ {c.last_message_at}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </GroupCard>

      <GroupCard header={<GroupHeading title="診察スケジュール" />}>
        {/* 長い枠リストでもホームが間延びしないよう、横並び+カード内スクロールにする */}
        <div className="grid gap-6 lg:grid-cols-2 items-start">
          <div>
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="text-base font-semibold text-[#1a1f36] truncate">{schedule_date.label}のスケジュール</h3>
                <button type="button" aria-label="前の日" className={dateNavButtonClass}
                        onClick={() => goScheduleDate(schedule_date.prev_value)}>◀</button>
                <button type="button" aria-label="次の日" className={dateNavButtonClass}
                        onClick={() => goScheduleDate(schedule_date.next_value)}>▶</button>
                {!schedule_date.is_today && (
                  <button type="button" className="text-sm text-blue-600 hover:underline cursor-pointer whitespace-nowrap"
                          onClick={() => goScheduleDate('')}>今日へ</button>
                )}
              </div>
              <a href={nav_paths.doctor_schedules} className="text-sm text-blue-600 hover:underline whitespace-nowrap">カレンダーで見る</a>
            </div>
            <TodaySchedule items={today_schedule} scheduleHref={nav_paths.doctor_schedules} scheduleDate={schedule_date} />
          </div>

          <div>
            <h3 className="text-base font-semibold text-[#1a1f36] mb-2">明日以降の診療予定</h3>
            <ExaminationList items={upcoming_examinations} emptyText="明日以降の診療予定はありません" />
            {/* サーバー側の表示上限（UPCOMING_EXAMINATIONS_LIMIT）に達した場合のみ、全予約と誤解されないよう注記する */}
            {upcoming_examinations.length >= 20 && (
              <p className="text-xs text-gray-400 mt-1">
                直近20件のみ表示しています。すべての予約は
                <a href={nav_paths.examinations} className="text-blue-600 hover:underline">診療管理</a>
                で確認できます
              </p>
            )}
          </div>
        </div>

        {/* 枠が不足している時間帯のレコメンド (#1076) */}
        {slot_shortages.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-base font-semibold text-[#1a1f36]">枠が不足している時間帯</h3>
              <a href={nav_paths.doctor_schedules} className="text-sm text-blue-600 hover:underline">カレンダーで見る</a>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              予約が入りやすいのに空き枠が足りない時間帯です。ご都合が合えば枠の開放にご協力ください。
            </p>
            <ul className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {slot_shortages.map((shortage, i) => (
                <li key={i} className="flex items-center gap-4 px-4 py-2">
                  <span className="text-sm font-semibold text-[#1a1f36] whitespace-nowrap">
                    {shortage.date_label} {shortage.time_range}
                  </span>
                  <span className="text-xs font-medium text-pink-700 bg-pink-50 border border-pink-200 rounded-full px-2 py-0.5 whitespace-nowrap">
                    あと{shortage.missing}枠
                  </span>
                  <button
                    type="button"
                    className="ml-auto text-sm text-blue-600 border border-blue-600 rounded-md px-3 py-1 hover:bg-blue-50 cursor-pointer whitespace-nowrap"
                    onClick={() => openShortageSlots(shortage)}
                  >
                    開放する
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </GroupCard>

      {reward_section && (
        <RewardSection
          section={reward_section}
          homeHref={nav_paths.home}
          scheduleHref={nav_paths.doctor_schedules}
          extraParams={schedule_date.is_today ? {} : { schedule_date: schedule_date.value }}
        />
      )}
    </>
  )
}

DoctorHome.layout = (page: ReactNode) => (
  <PersistentLayout>
    <DashboardLayout>{page}</DashboardLayout>
  </PersistentLayout>
)
